import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ChasseHuntMap, { createDivIcon } from '../components/ChasseHuntMap';
import Header from '../components/Header';
import { LangContext } from '../App';
import api from '../services/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const QC_CENTER = [52, -72];
const QC_ZOOM = 6;

const CATEGORIES = ['migrateur', 'sedentaire', 'spot_peche', 'etat_zone'];

const CAT_COLORS = {
  migrateur: '#2563eb',
  sedentaire: '#16a34a',
  spot_peche: '#0891b2',
  etat_zone: '#d97706',
};

// ── Translations ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Carte communautaire',
    sousTitre: 'Observations fauniques actives partagées par la communauté.',
    filtres: 'Filtres',
    category: 'Catégorie',
    toutesCategories: 'Toutes',
    species: 'Espèce',
    especePlaceholder: 'ex. orignal…',
    rayon: 'Rayon (km)',
    presDeM: '📍 Près de moi',
    geoChargement: 'Localisation…',
    geoErreur: 'GPS non available.',
    appliquer: 'Rechercher',
    reinitialiser: 'Réinitialiser',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger les observations.',
    aucune: 'Aucune observation dans ce secteur.',
    total: (n) => `${n} observation${n > 1 ? 's' : ''}`,
    categories: {
      migrateur: 'Migrateur',
      sedentaire: 'Sédentaire',
      spot_peche: 'Pêche',
      etat_zone: 'État zone',
    },
    // `species` is already declared above (line 35). Duplicate removed.
    level: 'Niveau',
    niveaux: {
      '1_publication': 'Niveau 1',
      '2_validation': 'Niveau 2',
      '3_chasse_reussie': 'Niveau 3',
    },
    expire: 'Expire le',
    distance: 'Distance',
    metres: 'm',
    positionRequise:
      'Activez la géolocalisation ou entrez des coordonnées pour lancer une recherche de proximité.',
    lat: 'Latitude',
    lng: 'Longitude',
  },
  en: {
    title: 'Community Map',
    sousTitre: 'Active wildlife observations shared by the community.',
    filtres: 'Filters',
    category: 'Category',
    toutesCategories: 'All',
    species: 'Species',
    especePlaceholder: 'e.g. moose…',
    rayon: 'Radius (km)',
    presDeM: '📍 Near me',
    geoChargement: 'Locating…',
    geoErreur: 'GPS unavailable.',
    appliquer: 'Search',
    reinitialiser: 'Reset',
    chargement: 'Loading…',
    erreur: 'Failed to load observations.',
    aucune: 'No observations in this area.',
    total: (n) => `${n} observation${n > 1 ? 's' : ''}`,
    categories: {
      migrateur: 'Migratory',
      sedentaire: 'Resident',
      spot_peche: 'Fishing',
      etat_zone: 'Zone status',
    },
    // `species` is already declared above (line 73). Duplicate removed.
    level: 'Level',
    niveaux: {
      '1_publication': 'Level 1',
      '2_validation': 'Level 2',
      '3_chasse_reussie': 'Level 3',
    },
    expire: 'Expires',
    distance: 'Distance',
    metres: 'm',
    positionRequise: 'Enable geolocation or enter coordinates to search nearby.',
    lat: 'Latitude',
    lng: 'Longitude',
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function makeIcon(category) {
  const color = CAT_COLORS[category] || '#888';
  return createDivIcon({
    html: `<div style="
      width:14px;height:14px;border-radius:50%;
      background:${color};border:2px solid #fff;
      box-shadow:0 1px 4px rgba(0,0,0,.5);
    "></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function CommunityMap() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;

  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(null);

  // Markers prêts à être passés à <ChasseHuntMap>. Filtre les lignes sans
  // coords + construit le popup React au lieu de générer du HTML inline.
  const observationsMarkers = useMemo(
    () =>
      observations
        .filter((obs) => obs.lat && obs.lng)
        .map((obs) => ({
          id: obs.id,
          lat: obs.lat,
          lng: obs.lng,
          icon: makeIcon(obs.category),
          popup: (
            <div style={{ minWidth: 180 }}>
              <strong style={{ color: CAT_COLORS[obs.category] || '#888' }}>
                {t.categories[obs.category] || obs.category}
              </strong>
              <div style={{ marginTop: 4 }}>
                <b>{t.species}:</b> {obs.species}
              </div>
              {obs.credits_level_reached && (
                <div>
                  <b>{t.level}:</b>{' '}
                  {t.niveaux[obs.credits_level_reached] || obs.credits_level_reached}
                </div>
              )}
              {obs.expires_at && (
                <div>
                  <b>{t.expire}:</b> {obs.expires_at}
                </div>
              )}
              {obs.distance_m !== undefined && (
                <div style={{ color: '#888', fontSize: '.8rem', marginTop: 4 }}>
                  {obs.distance_m} {t.metres}
                </div>
              )}
            </div>
          ),
        })),
    [observations, t]
  );

  // Filters
  const [category, setCategorie] = useState('');
  const [species, setEspece] = useState('');
  const [rayon, setRayon] = useState(25);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [geoLabel, setGeoLabel] = useState('');

  const load = useCallback(
    async (overrides = {}) => {
      const useLat = overrides.lat ?? lat;
      const useLng = overrides.lng ?? lng;

      if (!useLat || !useLng) return;

      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          lat: useLat,
          lng: useLng,
          rayon: (overrides.rayon ?? rayon) * 1000,
          limite: 200,
        });
        if (overrides.category ?? category) params.set('category', overrides.category ?? category);
        const { data } = await api.get(`/observations/proches?${params}`);
        let rows = data.data || [];
        if (species.trim()) {
          const q = species.trim().toLowerCase();
          rows = rows.filter((r) => r.species?.toLowerCase().includes(q));
        }
        setObservations(rows);
        setTotal(rows.length);
      } catch {
        setError(t.erreur);
      } finally {
        setLoading(false);
      }
    },
    [lat, lng, rayon, category, species, t.erreur]
  );

  const handleGeo = () => {
    if (!navigator.geolocation) {
      setGeoLabel(t.geoErreur);
      return;
    }
    setGeoLabel(t.geoChargement);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLat = pos.coords.latitude.toFixed(6);
        const newLng = pos.coords.longitude.toFixed(6);
        setLat(newLat);
        setLng(newLng);
        setGeoLabel('');
        load({ lat: newLat, lng: newLng });
      },
      () => setGeoLabel(t.geoErreur)
    );
  };

  const handleSearch = () => load();

  const handleReset = () => {
    setCategorie('');
    setEspece('');
    setRayon(25);
    setLat('');
    setLng('');
    setGeoLabel('');
    setObservations([]);
    setTotal(null);
  };

  return (
    <>
      <Header />
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '2rem 1rem' }}>
        <h1 style={{ color: 'var(--couleur-or)', marginBottom: '.25rem' }}>{t.title}</h1>
        <p style={{ color: 'var(--couleur-texte-attenué)', marginBottom: '1.5rem' }}>
          {t.sousTitre}
        </p>

        {/* Filters */}
        <section
          style={{
            background: 'var(--couleur-fond)',
            borderRadius: 'var(--rayon)',
            padding: '1.25rem',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gap: '.75rem',
              alignItems: 'end',
            }}
          >
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.category}
              <select
                value={category}
                onChange={(e) => setCategorie(e.target.value)}
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              >
                <option value="">{t.toutesCategories}</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t.categories[c]}
                  </option>
                ))}
              </select>
            </label>

            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.species}
              <input
                value={species}
                onChange={(e) => setEspece(e.target.value)}
                placeholder={t.especePlaceholder}
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              />
            </label>

            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.rayon}
              <select
                value={rayon}
                onChange={(e) => setRayon(parseInt(e.target.value, 10))}
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              >
                {[5, 10, 25, 50].map((r) => (
                  <option key={r} value={r}>
                    {r} km
                  </option>
                ))}
              </select>
            </label>

            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.lat}
              <input
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                placeholder="46.81"
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              />
            </label>

            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.lng}
              <input
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                placeholder="-71.21"
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              />
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
              <button
                onClick={handleGeo}
                style={{
                  padding: '.45rem .8rem',
                  background: '#1a3a2a',
                  color: '#4ade80',
                  border: '1px solid #4ade8044',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '.82rem',
                }}
              >
                {geoLabel || t.presDeM}
              </button>
              <div style={{ display: 'flex', gap: '.4rem' }}>
                <button
                  onClick={handleSearch}
                  disabled={!lat || !lng}
                  style={{
                    flex: 1,
                    padding: '.45rem',
                    background: lat && lng ? 'var(--couleur-or)' : '#333',
                    color: lat && lng ? '#000' : '#666',
                    border: 'none',
                    borderRadius: 6,
                    cursor: lat && lng ? 'pointer' : 'default',
                    fontWeight: 600,
                    fontSize: '.82rem',
                  }}
                >
                  {t.appliquer}
                </button>
                <button
                  onClick={handleReset}
                  style={{
                    padding: '.45rem .7rem',
                    background: 'transparent',
                    color: 'var(--couleur-texte-attenué)',
                    border: '1px solid #444',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: '.82rem',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
              marginTop: '1rem',
              fontSize: '.8rem',
            }}
          >
            {CATEGORIES.map((c) => (
              <span key={c} style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: CAT_COLORS[c],
                    display: 'inline-block',
                  }}
                />
                {t.categories[c]}
              </span>
            ))}
          </div>
        </section>

        {/* Status bar */}
        {!lat && !lng && (
          <p
            style={{
              color: 'var(--couleur-texte-attenué)',
              fontSize: '.88rem',
              marginBottom: '1rem',
            }}
          >
            {t.positionRequise}
          </p>
        )}
        {loading && <p style={{ color: 'var(--couleur-texte-attenué)' }}>{t.chargement}</p>}
        {error && <p style={{ color: 'var(--couleur-texte-erreur)' }}>{error}</p>}
        {total !== null && !loading && (
          <p
            style={{
              color: 'var(--couleur-texte-attenué)',
              fontSize: '.88rem',
              marginBottom: '.75rem',
            }}
          >
            {t.total(total)}
          </p>
        )}

        {/* Map */}
        <div
          style={{
            borderRadius: 'var(--rayon)',
            overflow: 'hidden',
            height: 520,
            border: '1px solid #2a2a2a',
          }}
        >
          {/* Carte clusterisée des observations communautaires (cluster=true
              wrap les markers dans MarkerClusterGroup côté composant). */}
          <ChasseHuntMap
            center={QC_CENTER}
            zoom={QC_ZOOM}
            height="100%"
            basemap="osm"
            cluster
            fitToMarkers={observations.length > 0}
            markers={observationsMarkers}
          />
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          <Link
            to="/"
            style={{ color: 'var(--couleur-or)', textDecoration: 'none', fontSize: '.9rem' }}
          >
            ← {lang === 'en' ? 'Home' : 'Accueil'}
          </Link>
        </div>
      </main>
    </>
  );
}
